import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useBranding } from "@/lib/branding";
import {
  ArrowRight,
  BookOpen,
  Check,
  Compass,
  GraduationCap,
  Sparkles,
  UserCheck,
} from "lucide-react";

/**
 * `/become-a-tax-pro` — Aspiring preparer path.
 * Sole focus: the 1040 course. NO 1120/AFSP/White-Label pitch —
 * those live on `/tax-pros` for seasoned professionals.
 */
export default function BecomeATaxPro() {
  const { branding } = useBranding();

  return (
    <div className="min-h-screen bg-background" data-testid="become-a-tax-pro-page">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">
                {branding.brand_name?.[0]}
              </div>
            )}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">Home</Link>
            <Link to="/tax-pros" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">For Tax Pros</Link>
            <Link to="/clients" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">Clients</Link>
            <Link to="/pricing" className="hidden md:inline underline text-primary hover:text-primary/80 font-medium">Pricing</Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(46,79,184,0.25), transparent 60%), linear-gradient(180deg, #0b1224 0%, #1e293b 100%)",
        }}
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-24 lg:py-32 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 text-primary text-xs uppercase tracking-widest mb-6">
            <Compass size={14} /> New to tax prep · Start here
          </div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] text-white mb-6">
            Ready to enter the tax industry?<br />
            <span className="italic text-accent">Master the 1040.</span>
          </h1>
          <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto mb-8">
            The Master the 1040 course walks you from "what is a W-2" all the way to filing 4 real practice returns — with an AI tax teacher inside every chapter, a live instructor to ask, and a printable certificate at the end.
          </p>

          <div className="mt-8 inline-flex items-baseline gap-3">
            <span className="font-serif text-6xl md:text-7xl text-white">$175</span>
            <span className="text-sm text-white/60">/ one-time · lifetime access</span>
          </div>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-white/90" data-testid="btp-cta-enroll">
              <Link to="/1040-course">See course details <ArrowRight size={16} className="ml-2" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10" data-testid="btp-cta-preview">
              <Link to="/course/1040">Preview 3 chapters free</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="border-t border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">What's inside</div>
            <h2 className="font-serif text-4xl md:text-5xl">Everything you need to file your first return.</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {WHATS_INSIDE.map((item, i) => (
              <div key={i} className="p-5 rounded-lg border border-border bg-card flex gap-4" data-testid={`btp-inside-${i}`}>
                <div className="h-11 w-11 rounded-md bg-primary/10 text-primary grid place-items-center shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-serif text-lg">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY BECOME A TAX PRO */}
      <section className="border-t border-border bg-secondary/40">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">Why this career</div>
            <h2 className="font-serif text-4xl md:text-5xl">
              Real earnings. <span className="italic text-primary">Real flexibility.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {WHY_BECOME.map((item, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`btp-why-${i}`}>
                <div className="mt-0.5 h-5 w-5 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                  <Check size={13} />
                </div>
                <span className="text-sm leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PATH FORWARD */}
      <section className="border-t border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16 text-center">
          <div className="label-caps mb-3 text-primary">Your next step</div>
          <h2 className="font-serif text-4xl md:text-5xl mb-4">
            Once you master the 1040, <span className="italic text-primary">the door opens.</span>
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
            After the 1040 course you can add the 1120 refresher for corporate returns, get AFSP-ready with our Circular 230 study pack, or launch your own branded tax business with our White-Label system.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg" data-testid="btp-final-cta">
              <Link to="/1040-course">Start the 1040 course <ArrowRight size={16} className="ml-2" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" data-testid="btp-cta-taxpros">
              <Link to="/tax-pros">Already a tax pro? Skip ahead</Link>
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-3">
          <div>Powered by <span className="text-foreground">{branding.brand_name}</span></div>
          <div className="flex gap-4">
            <Link to="/tax-pros" className="underline hover:text-foreground">Tax Pros</Link>
            <Link to="/clients" className="underline hover:text-foreground">Clients</Link>
            <Link to="/pricing" className="underline hover:text-foreground">Pricing</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

const WHATS_INSIDE = [
  { icon: <BookOpen size={22} />, title: "13 in-depth chapters", body: "Every line of the 1040, plus Schedules 1-3, credits (EITC/CTC/AOTC/LLC), Schedule A itemized deductions, and Schedule C basics." },
  { icon: <GraduationCap size={22} />, title: "4 live practice returns", body: "Fill out actual 1040s for real-world scenarios — from a simple W-2 filer to a self-employed rideshare driver." },
  { icon: <Sparkles size={22} />, title: "AI Tax Teacher inside every chapter", body: "Stuck on a concept? Ask Claude Sonnet 5 for a plain-English explanation. Available 24/7 without waiting on an instructor." },
  { icon: <UserCheck size={22} />, title: "Ask a human instructor", body: "Whenever you want a human sanity check, message Di'Meka directly. Answers usually within a business day." },
];

const WHY_BECOME = [
  "The IRS reports over 700,000 preparers filing returns each year — clients need more preparers, not fewer.",
  "Set your own hours during tax season and work virtually from anywhere in the U.S.",
  "Charge $150–$500+ per return once you're established. 200 returns/year is a solid side income.",
  "Add credentials over time (AFSP, EA, CPA) to grow your rate and unlock representation rights before the IRS.",
  "Every dollar of prep fees stays with you — no franchise fees, no upline splits.",
  "Own tax knowledge for life. Even if you never file for clients, you'll save thousands on your own returns.",
];
